import { FC, useEffect } from 'react';
import { createUseStyles } from 'react-jss';
import { InnerhivePage } from '../InnerhivePage';
import { IonButtons, IonToolbar } from '@ionic/react';
import { Text } from '../components/Text';
import { ActiveCareMapProviderV2 } from '../ActiveCareMapProvider';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { CareMapState } from '../caremap/CareMapState';
import { ManageButton } from './ManageButton';
import { ICareMap } from '../caremap/ICareMap';
import { CaremapNotesModelFactory } from '../notes/model/CaremapNotesModelFactory';
import { RemoveMeButton } from './RemoveMeButton';
import { CommunityFeedList } from './CommunityFeedList';
import { ICaremapNote } from '../notes/model/NotesClient';
import { AddCommunityPostWithQueryParamDrawer } from './AddCommunityPostWithQueryParamDrawer';
import { CommunityModelFactory } from '../share-caremap/CommunityModelFactory';
import { ViewCommunityPostWithQueryParamDrawer } from './ViewCommunityPostWithQueryParamDrawer';
import { AddPostButton } from './AddPostButton';

const useStyles = createUseStyles(() => ({
    toolbar: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },

    image: {
        maxWidth: 500,
        margin: 'auto',
        padding: '30px 60px 0 20px'
    }
}));

export const CommunityFeedPage: FC<{ careMapId: string; }> = ({ careMapId }) => {
    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    return (
        <ActiveCareMapProviderV2 careMapId={careMapId}>
            <CommunityFeedPageWithCareMap careMapId={careMapId} />
        </ActiveCareMapProviderV2>
    );
};

const CommunityFeedPageWithCareMap: FC<{ careMapId: string; }> = ({ careMapId }) => {
    const classes = useStyles();

    const careMapState = useInstance(CareMapState);

    const addPostDrawer = useInstance(AddCommunityPostWithQueryParamDrawer);
    const viewPostDrawer = useInstance(ViewCommunityPostWithQueryParamDrawer);

    const factory = useInstance(CommunityModelFactory);
    useSubscription(() => factory);
    const community = factory.forActiveCaremap();

    const notesModelFactory = useInstance(CaremapNotesModelFactory);
    useSubscription(() => notesModelFactory);

    // load data on mount
    useEffect(() => {
        community.load();
    }, [community]);

    const careMap = careMapState.getActiveCaremap() as ICareMap;
    if (!careMap){
        return null;
    }

    const model = notesModelFactory.forActiveCaremap();
    let posts = model.getNotes();

    // never show private notes on community page
    posts = posts.filter(post => !post.isPrivate);

    const hasPosts = (posts.length > 0);

    const name = careMapState.getPatientFirstName(careMap);
    const supportMsg = (name ? `supporting ${name}` : 'your support');

    const collaboratorCount = community.getCollaboratorCount();
    const followers = community.isLoaded() && collaboratorCount ? `${collaboratorCount} Supporters` : '';

    const self = Boolean(careMap.initialWizardAnswers['recipient-self']);
    const whose = (name && !self ? `${name}'s` : 'your');

    const canManage = Boolean(careMap.links.permissions || careMap.links.createViewerAccessToken);
    const canEdit = careMap.links.self.actions.includes('edit-notes');

    const addComment = (post: ICaremapNote) => {
        viewPostDrawer.open({ postId: post.noteId, addComment: true });
    };

    const viewPost = (post: ICaremapNote) => {
        viewPostDrawer.open({ postId: post.noteId });
    };

    const addPost = () => {
        addPostDrawer.open();
    };

    if (!model.hasLoaded) {
        return null;
    }

    return (
        <InnerhivePage
            data-id="community-feed-page"
            title="Community"
        >
            {(hasPosts || !canManage) && (
                <IonToolbar>
                    <div className={classes.toolbar} data-loaded={community.isLoaded()}>
                        {followers && (
                            <Text type="detail">{followers}</Text>
                        )}&nbsp;
                        <IonButtons slot="end">
                            {canManage && hasPosts && (
                                <ManageButton careMapId={careMapId} />
                            )}
                            {!canManage && (
                                <RemoveMeButton />
                            )}
                        </IonButtons>
                    </div>
                </IonToolbar>
            )}

            {canEdit && (
                <AddPostButton onClick={addPost} />
            )}

            {!hasPosts && !canEdit && (
                <>
                    <p>
                        <Text>
                            Thank you so much for {supportMsg}! There are no community posts yet – be sure to check back soon.
                        </Text>
                    </p>
                    <div className={classes.image}>
                        <img src="/assets/images/share/spread-love.png" alt="" />
                    </div>
                </>
            )}

            {!hasPosts && canEdit && (
                <>
                    <p>
                        <Text>
                            Welcome your growing community to Innerhive with an introductory post. Share a brief overview of {whose} care situation,
                            explain how Innerhive will keep everyone in the loop, and set the tone for a connected care team!
                        </Text>
                    </p>
                    <p>
                        <Text type="subtitle">
                            <i>Example:</i>
                        </Text>
                        <Text type="detail">
                            <i style={{fontSize: 14}}>
                                Hi everyone! I wanted to share an update about Grandpa Joe. He's recently been diagnosed with dementia, and I'll be
                                stepping in to help with his daily care. He has many upcoming appointments and a large care team to keep track of,
                                so I'll be using Innerhive to stay organized and keep you all informed along the way. Your support and messages will
                                be truly appreciated, even if I'm not always able to reply.
                            </i>
                        </Text>
                    </p>
                    <div className={classes.image}>
                        <img src="/assets/images/share/spread-love.png" alt="" />
                    </div>
                </>
            )}

            {notesModelFactory.forActiveCaremap().hasLoaded && (
                <CommunityFeedList
                    posts={posts}
                    postClick={viewPost}
                    addComment={addComment}
                />
            )}
        </InnerhivePage>
    );
};
